import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { DayType } from '@/app/constants/dayType'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { SortDirection, TableHeaderColumn } from '@/modules/drivers/types/table'
import { Checkbox } from '@/ui'

type Props = {
  children?: React.ReactNode
  displayBailiffDeductionAmount?: boolean
  displayEquivalent?: boolean
  dynamicLeaveDayTypes?: number[]
  sortBy?: string | null
  sortAsc?: boolean
  onHeaderClick?: (key: string) => void
  includeAllBusinessTrips?: boolean
  includeSomeBusinessTrips?: boolean
  onToggleAllBusinessTrips?: (next: boolean) => void
}

export const PayrollResultsTableWrapper = ({
  children,
  displayBailiffDeductionAmount,
  displayEquivalent,
  dynamicLeaveDayTypes = [],
  sortBy,
  sortAsc,
  onHeaderClick,
  includeAllBusinessTrips = false,
  includeSomeBusinessTrips = false,
  onToggleAllBusinessTrips,
}: Props) => {
  const { t, tSafe } = useLocalize()

  const columns: TableHeaderColumn[] = React.useMemo(() => {
    const cols: TableHeaderColumn[] = [
      {
        title: t('timesheet.col-full-name'),
        key: 'fullName',
        type: 'string',
        sort: true,
      },
    ]
    if (displayBailiffDeductionAmount) {
      cols.push({
        title: t('timesheet.bailiff-deduction'),
        key: 'bailiffDeduction',
        type: 'number',
        sort: true,
      })
    }
    if (displayEquivalent) {
      cols.push({
        title: t('timesheet.finalLeavesGrossHours'),
        key: 'finalLeavesGrossHours',
        type: 'number',
        sort: false,
      })
      cols.push({
        title: t('timesheet.finalLeavesGrossAmount'),
        key: 'finalLeavesGrossAmount',
        type: 'number',
        sort: false,
      })
    }
    cols.push(
      {
        title: t('timesheet.col-vacation-gross'),
        key: 'offPay',
        type: 'number',
        sort: true,
      },
      {
        title: t('timesheet.col-sick-leave-gross'),
        key: 'sickPay',
        type: 'number',
        sort: true,
      },
      {
        title: t('timesheet.col-benefits'),
        key: 'benefits',
        type: 'number',
        sort: true,
      },
      {
        title: t('timesheet.col-bonus'),
        key: 'bonus',
        type: 'number',
        sort: true,
      },
      {
        title: t('timesheet.col-costs'),
        key: 'costs',
        type: 'number',
        sort: true,
      },
      {
        title: t('timesheet.col-domestic-delegations'),
        key: 'businessTrip',
        type: 'number',
        sort: true,
        headerContent: (
          <span className="tw-flex tw-items-center tw-gap-2">
            <span>{t('timesheet.col-domestic-delegations')}</span>
            <label
              className="tw-mb-0 tw-flex tw-cursor-pointer tw-items-center tw-gap-1"
              title={tSafe(
                'timesheet.include-all-domestic-trips-tooltip',
                'Zaznacz "Wliczaj" dla wszystkich kierowców na liście',
              )}
              onClick={(e) => e.stopPropagation()}
            >
            
              <Checkbox
                name="includeAllBusinessTrips"
                checked={includeAllBusinessTrips || includeSomeBusinessTrips}
                indeterminate={
                  includeSomeBusinessTrips && !includeAllBusinessTrips
                }
                onChange={() =>
                  onToggleAllBusinessTrips?.(!includeAllBusinessTrips)
                }
              />
              <span>Wliczaj</span>
            </label>
          </span>
        ),
      },
      {
        title: t('timesheet.col-expected-netto'),
        key: 'expectedNetto',
        type: 'number',
        sort: true,
      },
      {
        title: t('timesheet.col-calculated-netto'),
        key: 'calculatedNetto',
        type: 'number',
        sort: true,
      },
      {
        title: t('timesheet.col-reward'),
        key: 'reward',
        type: 'number',
        sort: true,
      },
      {
        title: t('timesheet.col-gross-total'),
        key: 'summary',
        type: 'number',
        sort: true,
      },
      {
        title: t('timesheet.col-mandate'),
        key: 'mandate',
        type: 'number',
        sort: true,
      },
      {
        title: t('timesheet.col-advance'),
        key: 'advance',
        type: 'number',
        sort: true,
      },
    )
    for (const dt of dynamicLeaveDayTypes) {
      const code = DayType[dt]
      cols.push({
        title: code ? t(`dayType.${code}`) : String(dt),
        key: `dayType-${dt}`,
        type: 'number',
        sort: false,
      })
    }
    return cols
  }, [
    t,
    tSafe,
    displayBailiffDeductionAmount,
    displayEquivalent,
    dynamicLeaveDayTypes,
    includeAllBusinessTrips,
    includeSomeBusinessTrips,
    onToggleAllBusinessTrips,
  ])

  const sortConfig = sortBy
    ? { key: sortBy, direction: (sortAsc ? 'asc' : 'desc') as SortDirection }
    : null

  return (
    <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
      <TableHeader
        opacity
        rounded={false}
        disabledCheckboxToggle
        maxWidthOff
        columns={columns}
        sortConfig={sortConfig}
        sticky
        onHeaderClick={onHeaderClick}
      />
      {children && <tbody>{children}</tbody>}
    </table>
  )
}
